<template>
    <div id="payyo-settings"
         class="modl-tab-body">
        <div class="modl-body">
            <form class="form-h"
                  @submit.prevent="submit">
                <div v-if="saveError"
                     class="alert alert-danger">
                    <p>{{ saveError }}</p>
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('PAYYO_SETTINGS.TERMINAL_MANAGEMENT__LABEL') }}
                    </label>
                    <div class="form-h__right">
                        <div class="payyo-utility-buttons">
                            <SquareBtn size="lg"
                                       color="info"
                                       @click="register()">
                                {{ l('PAYYO_SETTINGS.REGISTER_TERMINAL_BTN') }}
                            </SquareBtn>
                        </div>
                    </div>
                </div>

                <hr />

                <div class="form-group">
                    <label for="payyo-device-id"
                           class="control-label">
                        {{ l('PAYYO_SETTINGS.DEVICE_ID__LABEL') }}
                    </label>
                    <div class="form-h__right">
                        <div class="input-group-flex">
                            <input id="payyo-device-id"
                                   v-model="devicePayyoConfig.deviceId"
                                   type="text"
                                   disabled="disabled"
                                   class="form-control input-lg">
                            <button class="btn btn-info"
                                    @click.prevent="showTerminals()">
                                {{ l('PAYYO_SETTINGS.VIEW_TERMINALS_BTN') }}
                            </button>
                        </div>
                    </div>
                </div>

                <template v-if="companyPayyoConfig">
                    <div class="form-group">
                        <label for="payyo-api-key"
                               class="control-label">
                            {{ l('PAYYO_SETTINGS.API_KEY') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input id="payyo-api-key"
                                   v-model="devicePayyoConfig.apiKey"
                                   type="text"
                                   class="form-control input-lg">
                            <PaymentConfigDefaultValue config-key="apiKey"
                                                       :show-value="false"
                                                       :device-config="devicePayyoConfig"
                                                       :company-config="companyPayyoConfig" />
                        </div>
                    </div>

                    <!-- We could add additional config options for apiBaseUrl and apiSocketUrl if needed -->

                    <div class="form-group">
                        <label for="payyo-api-url-path"
                               class="control-label">
                            {{ l('PAYYO_SETTINGS.API_URL_PATH') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input id="payyo-api-url-path"
                                   v-model="devicePayyoConfig.apiUrlPath"
                                   type="text"
                                   class="form-control input-lg">
                            <PaymentConfigDefaultValue config-key="apiUrlPath"
                                                       :device-config="devicePayyoConfig"
                                                       :company-config="companyPayyoConfig" />
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="payyo-merchant-id"
                               class="control-label">
                            {{ l('PAYYO_SETTINGS.MERCHANT_ID__LABEL') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input id="payyo-merchant-id"
                                   v-model="companyPayyoConfig.merchantId"
                                   type="text"
                                   disabled="disabled"
                                   class="form-control input-lg">
                            <p class="help-block">
                                {{ l('PAYYO_SETTINGS.COMPANY_WIDE_SETTING') }}
                            </p>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="payyo-platform-id"
                               class="control-label">
                            {{ l('PAYYO_SETTINGS.PLATFORM_ID__LABEL') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input id="payyo-platform-id"
                                   v-model="companyPayyoConfig.platformId"
                                   type="text"
                                   disabled="disabled"
                                   class="form-control input-lg">
                            <p class="help-block">
                                {{ l('PAYYO_SETTINGS.COMPANY_WIDE_SETTING') }}
                            </p>
                        </div>
                    </div>

                    <hr />

                    <div class="form-group">
                        <label class="control-label">
                            {{ l('PAYYO_SETTINGS.CONFIGURE_TIPS__LABEL') }}
                        </label>
                        <div class="form-h__right">
                            <div class="payyo-utility-buttons">
                                <SquareBtn size="lg"
                                           color="info"
                                           @click="configureTips()">
                                    <i class="fa-regular fa-coins"></i>
                                    {{ l('PAYYO_SETTINGS.CONFIGURE_TIPS_BTN') }}
                                </SquareBtn>
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <label class="control-label">
                            {{ l('PAYYO_SETTINGS.RECENT_PAYMENTS__LABEL') }}
                        </label>
                        <div class="form-h__right">
                            <div class="payyo-utility-buttons">
                                <SquareBtn data-testid="payyo-recent-transactions-button"
                                           size="lg"
                                           color="info"
                                           :disabled="!devicePayyoConfig.deviceId"
                                           @click="recentTransactions()">
                                    <i class="fa-solid fa-search"></i>
                                    {{ l('PAYYO_SETTINGS.RECENT_PAYMENTS_BTN') }}
                                </SquareBtn>
                            </div>
                        </div>
                    </div>
                </template>
            </form>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button class="btn btn-lg btn-primary"
                    :disabled="saving"
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-regular fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import PaymentConfigDefaultValue from '@/epos/vue/modals/Settings/components/PaymentConfigDefaultValue';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import PayyoConfig from '@/lib/PaymentMethods/Payyo/PayyoConfig';
import SquareBtn from '@/vue/components/SquareBtn';
import { apiResponseToError } from '@/lib/Api';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { removeEmptyValues } from '@/functions/objects';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

/**
 * This uses some non-translated strings because it's really for internal use only.
 */
export default {
    name: 'PayyoSettingsTab',

    components: { PaymentConfigDefaultValue, SquareBtn },

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            saving: false,
            saveError: null,

            companyPayyoConfig: null,

            // **This is the device config only**.
            devicePayyoConfig: {
                deviceId: null,
            },
            device: null,
            payyoConfig: null,
            paymentMethodRef: PaymentMethodRef.CARD_PAYYO_CHIP_AND_PIN,
        };
    },

    created() {
        // Load the payment method.
        const paymentMethod = container.paymentMethodService.getByRef(this.paymentMethodRef);
        if (!paymentMethod) {
            // Throw an error if the payment method is not enabled (how did we get here though?)
            throw new Error(
                container.langService.getText('PAYYO_SETTINGS.ERROR_PAYYO_DISABLED'),
            );
        }

        // Set the existing company-level config in the Vue data.
        // This is for reference in the UI only and will not be modified here.
        this.companyPayyoConfig = paymentMethod.config ? JSON.parse(paymentMethod.config) : {};

        // Clean the company config in case empty keys have been set in Backoffice.
        removeEmptyValues(this.companyPayyoConfig);

        /**
         * @type {!Device}
         */
        this.device = container.currentDevice.device;

        // Copy any existing values from the device's paymentTerminalConfig.payyo object
        // to the new config (this.devicePayyoConfig).
        if (this.device.paymentTerminalConfig?.[this.paymentMethodRef]) {
            Object.entries(this.device.paymentTerminalConfig[this.paymentMethodRef])
                .forEach(([key, value]) => {
                    if (value || value === false) {
                        this.devicePayyoConfig[key] = value;
                    }
                });
        }

        this.payyoConfig = new PayyoConfig();
    },

    methods: {
        async submit() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.saveError = null;

            // Build a new config object containing just the device config we want to save.
            const newDeviceConfig = {};
            // Build a new combined company + device config object from the form data so that we can validate it.
            const newCombinedConfig = {};
            Object.keys(this.companyPayyoConfig).forEach((key) => {
                if (this.companyPayyoConfig[key] || this.companyPayyoConfig[key] === false) {
                    newCombinedConfig[key] = this.companyPayyoConfig[key];
                }
            });

            Object.keys(this.devicePayyoConfig).forEach((key) => {
                if (this.devicePayyoConfig[key] || this.devicePayyoConfig[key] === false) {
                    newCombinedConfig[key] = this.devicePayyoConfig[key];
                    newDeviceConfig[key] = this.devicePayyoConfig[key];
                }
            });

            // Clean the combined values (e.g. no keys that are just spaces).
            removeEmptyValues(newCombinedConfig);
            console.log('Validating new Payyo config.', newCombinedConfig);

            const configInstance = new PayyoConfig();

            // Validate the new config.
            try {
                container.hydrator.hydrate(newCombinedConfig, configInstance);
                configInstance.validate(false);
            } catch (e) {
                this.saveError = e.message;
                this.saving = false;

                return;
            }

            // Send the new settings to the API to be saved on the device.
            // Remember, we only want to update the Payyo settings not anything else that is stored in the config,
            // so the new value starts as the existing value, and we only replace one key.
            let newDevicePaymentTerminalConfig = this.device.paymentTerminalConfig;

            // In case there is no existing JSON in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig) {
                newDevicePaymentTerminalConfig = {};
            }
            // In case there is no existing Payyo key in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig.hasOwnProperty(this.paymentMethodRef)) {
                newDevicePaymentTerminalConfig[this.paymentMethodRef] = {};
            }
            newDevicePaymentTerminalConfig[this.paymentMethodRef] = newDeviceConfig;

            const newDevicePaymentTerminalConfigJson = JSON.stringify(newDevicePaymentTerminalConfig);

            container.deviceResource.setPaymentTerminalConfig(
                {
                    deviceID: this.device.ID,
                },
                {
                    paymentTerminalConfig: newDevicePaymentTerminalConfigJson,
                },
                async () => {
                    // Set the new values on the current device so they are populated if the settings modal is reopened,
                    // without having to reload the app.
                    /**
                     * @type {CurrentDevice}
                     */
                    const currentDevice = container.currentDevice;
                    // Convert it back from JSON instead of using the object so that it is a clone and doesn't
                    // contain anything like Vue's reactivity.
                    currentDevice.device.paymentTerminalConfig = JSON.parse(newDevicePaymentTerminalConfigJson);

                    // Save the device to storage.
                    currentDevice.cache();

                    // Update the config in the driver, so we can make an API request to set terminalID if necessary.
                    configInstance.username = String(currentDevice.ID);
                    container.payyoDriver.config = configInstance;
                    await container.payyoWebSocketClient.setConfig(configInstance);

                    this.saving = false;

                    if (!configInstance.deviceId) {
                        this.saveError = container.langService.getText('PAYYO_SETTINGS.SELECT_TERMINAL');
                        return;
                    }

                    // Because we've already updated the config in the driver we don't need to tell the user to sync.
                    container.toastFactory.success(
                        'PAYYO_SETTINGS.SUCCESS_TOAST_TITLE',
                    );

                    this.$emit('resolve');
                },
                (result) => {
                    this.saveError = apiResponseToError(result).message;
                    this.saving = false;
                },
            );
        },

        async showTerminals() {
            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.PAYYO_TERMINALS,
                {
                    deviceId: this.devicePayyoConfig.deviceId || null,
                },
            ).then((deviceId) => {
                if (deviceId) {
                    // Clear existing errors.
                    this.saveError = null;
                    this.devicePayyoConfig.deviceId = deviceId;
                }
            }).catch(suppressModalCancelledErrorOrReThrow);
        },

        async register() {
            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.PAYYO_TERMINAL_REGISTRATION,
                {},
            ).then((deviceId) => {
                if (deviceId) {
                    // Clear existing errors.
                    this.saveError = null;
                    this.devicePayyoConfig.deviceId = deviceId;
                }
            }).catch(suppressModalCancelledErrorOrReThrow);
        },

        async configureTips() {
            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.PAYYO_CONFIGURE_TIPS,
            ).then((result) => {
                if (result) {
                    // Clear existing errors.
                    this.saveError = null;
                }
            }).catch(suppressModalCancelledErrorOrReThrow);
        },

        async recentTransactions() {
            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.PAYYO_MANUAL_RECONCILIATION, {
                    deviceId: this.devicePayyoConfig.deviceId || null,
                    showButtons: false,
                    filterPayments: false,
                }
            ).catch(suppressModalCancelledErrorOrReThrow);
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables.less";

.payyo-utility-buttons {
    display: flex;
    justify-content: flex-start;
    grid-gap: 15px;
}

@media (max-width: @mobile-max) {
    .payyo-utility-buttons {
        flex-direction: column;
    }
}
</style>
